import { memo, useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { resolveUiLocale } from '@/utils/uiLocale';

import type { ConsensusDemandPoint } from '../types/consensus.types';
import {
  type ChartGranularity,
  formatChartBucketLabel,
  formatChartTooltipLabel,
} from '../utils/chartLabels';

function toNumber(value: number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  return Number.isFinite(value) ? value : null;
}

interface Props {
  data: ConsensusDemandPoint[];
  currentPeriod?: string | null;
  granularity?: ChartGranularity;
  className?: string;
  height?: number;
}

function ConsensusDemandChartInner({
  data,
  currentPeriod,
  granularity = 'week',
  className = '',
  height = 360,
}: Props) {
  const { t, i18n } = useTranslation();
  const locale = resolveUiLocale(i18n.language);

  const points = useMemo(
    () =>
      data.map((p) => ({
        time_bucket: p.time_bucket,
        actual: toNumber(p.actual_qty),
        baseline: toNumber(p.baseline_qty),
        sop_impact: toNumber(p.assumption_impact_qty),
        consensus: toNumber(p.consensus_qty),
      })),
    [data],
  );

  const formatX = (value: string) => formatChartBucketLabel(value, granularity, locale);

  return (
    <div className={`min-w-0 w-full max-w-full ${className}`} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={points} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis
            dataKey="time_bucket"
            tickFormatter={formatX}
            tick={{ fontSize: 10, fill: '#64748b' }}
            minTickGap={32}
            interval="preserveStartEnd"
            angle={granularity === 'week' ? -35 : 0}
            textAnchor={granularity === 'week' ? 'end' : 'middle'}
            height={granularity === 'week' ? 56 : 32}
          />
          <YAxis tick={{ fontSize: 11, fill: '#64748b' }} width={56} />
          <Tooltip
            labelFormatter={(value) =>
              formatChartTooltipLabel(String(value), granularity, locale)
            }
            formatter={(value: number, name: string) => [
              Number(value).toLocaleString(locale, { maximumFractionDigits: 0 }),
              name,
            ]}
          />
          <Legend />
          {currentPeriod ? (
            <ReferenceLine
              x={currentPeriod}
              stroke="#94a3b8"
              strokeDasharray="4 4"
              label={{
                value:
                  granularity === 'month'
                    ? t('consensus.currentMonth')
                    : t('consensus.currentWeek'),
                position: 'insideTopRight',
                fill: '#64748b',
                fontSize: 11,
              }}
            />
          ) : null}
          <Line
            type="monotone"
            dataKey="actual"
            name={t('consensus.history')}
            stroke="#475569"
            strokeWidth={2}
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
          <Bar
            dataKey="baseline"
            name={t('consensus.baseline')}
            stackId="plan"
            fill="#94a3b8"
            isAnimationActive={false}
          />
          <Bar
            dataKey="sop_impact"
            name={t('consensus.sopImpact')}
            stackId="plan"
            fill="#38bdf8"
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="consensus"
            name={t('consensus.demandPlanTotal')}
            stroke="#0f172a"
            strokeWidth={2.5}
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export const ConsensusDemandChart = memo(ConsensusDemandChartInner);
